import { Theme } from "api/config/chalice-types";
import { useMemo, useState } from "react";

interface UseFilteredSortedThemesProps {
  allThemes: Theme[];
  allThemesLoading: boolean;
  selectedThemes: string[];
}

const useFilteredSortedThemes = ({
  allThemes,
  allThemesLoading,
  selectedThemes,
}: UseFilteredSortedThemesProps) => {
  const [sortOption, setSortOption] = useState<
    "release-date" | "name" | "selected"
  >("release-date");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);

  const allCategories = useMemo(() => {
    if (allThemesLoading) return [];
    const categoriesSet = new Set<string>();
    allThemes.forEach((theme) => {
      if (theme.category) categoriesSet.add(theme.category);
    });
    return Array.from(categoriesSet).sort();
  }, [allThemes, allThemesLoading]);

  const allStyles = useMemo(() => {
    if (allThemesLoading) return [];
    const stylesSet = new Set<string>();
    allThemes.forEach((theme) => {
      (theme.style || []).forEach((style: string) => stylesSet.add(style));
    });
    return Array.from(stylesSet).sort();
  }, [allThemes, allThemesLoading]);

  const filteredSortedThemes = useMemo(() => {
    if (allThemesLoading) return [];

    let processedThemes: Theme[] = [...allThemes];

    processedThemes = processedThemes.filter((theme) => {
      const categoryMatch =
        selectedCategories.length === 0 ||
        (theme.category && selectedCategories.includes(theme.category));
      if (!categoryMatch) return false;

      const styleMatch =
        selectedStyles.length === 0 ||
        selectedStyles.some((style) => (theme.style || []).includes(style));
      if (!styleMatch) return false;

      return true;
    });

    processedThemes.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aIsSelected = selectedThemes.includes(a.name);
      const bIsSelected = selectedThemes.includes(b.name);
      const aDate = a.created_at;
      const bDate = b.created_at;

      switch (sortOption) {
        case "selected":
          if (aIsSelected !== bIsSelected) {
            return aIsSelected ? -1 : 1;
          }
          return aName.localeCompare(bName);
        case "name":
          return aName.localeCompare(bName);
        case "release-date":
        default:
          if (aDate && bDate) {
            try {
              const dateA = new Date(aDate).getTime();
              const dateB = new Date(bDate).getTime();
              if (dateB !== dateA) return dateB - dateA;
            } catch (e) {
              console.warn(
                "Could not parse theme dates for sorting:",
                aDate,
                bDate
              );
            }
          } else if (aDate) {
            return -1;
          } else if (bDate) {
            return 1;
          }
          return aName.localeCompare(bName);
      }
    });

    return processedThemes;
  }, [
    allThemes,
    selectedThemes,
    sortOption,
    selectedCategories,
    selectedStyles,
    allThemesLoading,
  ]);

  return {
    filteredSortedThemes,
    sortOption,
    setSortOption,
    selectedCategories,
    setSelectedCategories,
    allCategories,
    selectedStyles,
    setSelectedStyles,
    allStyles,
  };
};

export default useFilteredSortedThemes;
